import { t as getLogger } from "./logger-CpoV6RQj.mjs";
import { n as WINDOW } from "./errorManager-CoSVVhi4.mjs";
import { t as getRequestMonitor } from "./requestMonitor-BI0Un5F-.mjs";

//#region src/interceptors/fetch/index.ts
var FetchInterceptor = class {
	name = "fetch";
	config;
	cleanup = null;
	logger = getLogger();
	constructor(config) {
		this.config = {
			timeoutMs: 3e4,
			showSlowRequestAlerts: true,
			showErrorAlerts: true,
			monitoredServicePatterns: [],
			monitoredServiceOptions: {},
			...config
		};
	}
	setup() {
		if (this.cleanup) {
			this.logger.warn("Fetch interceptor already set up");
			return this.cleanup;
		}
		try {
			this.cleanup = this.setupInternal();
			return this.cleanup;
		} catch (error) {
			this.logger.error("Fetch interceptor setup failed:", error);
			return () => {};
		}
	}
	setupInternal() {
		if (!WINDOW.fetch || typeof WINDOW.fetch !== "function") {
			this.logger.warn("fetch not available, skipping interceptor setup");
			return () => {};
		}
		const originalFetch = WINDOW.fetch.bind(WINDOW);
		let requestMonitor;
		try {
			requestMonitor = getRequestMonitor({
				timeoutMs: this.config.timeoutMs,
				showSlowRequestAlerts: this.config.showSlowRequestAlerts,
				showErrorAlerts: this.config.showErrorAlerts,
				monitoredServicePatterns: this.config.monitoredServicePatterns,
				monitoredServiceOptions: this.config.monitoredServiceOptions
			});
		} catch (error) {
			this.logger.error("Failed to initialize request monitor:", error);
			return () => {};
		}
		const interceptedFetch = async (input, init) => {
			let url = "";
			try {
				url = input instanceof Request ? input.url : input.toString();
			} catch (error) {
				this.logger.debug("Failed to extract URL:", error);
				return originalFetch(input, init);
			}
			let shouldMonitor = false;
			try {
				shouldMonitor = requestMonitor.isMonitored(url);
			} catch (error) {
				this.logger.warn("Error checking if URL should be monitored:", error);
				return originalFetch(input, init);
			}
			if (!shouldMonitor) return originalFetch(input, init);
			let timeoutId;
			if (this.config.showSlowRequestAlerts && this.config.timeoutMs && this.config.timeoutMs > 0) try {
				timeoutId = WINDOW.setTimeout(() => {
					try {
						requestMonitor.handleSlowRequest(url, "fetch");
					} catch (error) {
						this.logger.debug("Error handling slow request:", error);
					}
				}, this.config.timeoutMs);
			} catch (error) {
				this.logger.debug("Error setting up timeout monitoring:", error);
			}
			try {
				const response = await originalFetch(input, init);
				if (timeoutId !== void 0) WINDOW.clearTimeout(timeoutId);
				if (this.config.showErrorAlerts && response.status >= 400) WINDOW.queueMicrotask(() => {
					try {
						requestMonitor.handleHttpError(response.status, response.statusText, url, "fetch");
					} catch (error) {
						this.logger.warn("Error in HTTP error handler:", error);
					}
				});
				return response;
			} catch (error) {
				if (timeoutId !== void 0) WINDOW.clearTimeout(timeoutId);
				if (this.config.showErrorAlerts) WINDOW.queueMicrotask(() => {
					try {
						const errorMessage = error instanceof Error ? error.message : "Network request failed";
						requestMonitor.handleNetworkError(errorMessage, url, "fetch", error instanceof Error ? error : new Error(errorMessage));
					} catch (handlerError) {
						this.logger.warn("Error in network error handler:", handlerError);
					}
				});
				throw error;
			}
		};
		WINDOW.fetch = interceptedFetch;
		return () => {
			try {
				WINDOW.fetch = originalFetch;
				this.cleanup = null;
			} catch (error) {
				this.logger.error("Error during fetch interceptor cleanup:", error);
			}
		};
	}
};
/**
* Factory function to create and setup a fetch interceptor.
*
* @param config - Configuration options for the interceptor
* @returns Cleanup function to restore original fetch behavior
*/
function setupFetchInterceptor(config) {
	return new FetchInterceptor(config).setup();
}

//#endregion
export { FetchInterceptor, setupFetchInterceptor };